import { Effect } from 'effect';
import { BRANDS, OKRAPDF_BRAND } from './packs';
import type { BrandConfig, BrandId } from './types';

let cached: BrandConfig | null = null;

function readViteBrandId(): BrandId | undefined {
  try {
    const value = import.meta.env?.VITE_BRAND;
    if (value === 'okrapdf' || value === 'parsegrid') return value;
  } catch {
    // Worker or non-Vite contexts have no import.meta.env.VITE_*.
  }
  return undefined;
}

export function brandByHostnameEffect(
  hostname: string | undefined,
): Effect.Effect<BrandConfig | undefined> {
  return Effect.sync(() => {
    if (!hostname) return undefined;
    for (const brand of Object.values(BRANDS)) {
      if (
        brand.hosts.apex.includes(hostname) ||
        brand.hosts.www.includes(hostname) ||
        brand.hosts.app.includes(hostname)
      ) {
        return brand;
      }
    }
    return undefined;
  });
}

export function brandByHostname(hostname: string | undefined): BrandConfig | undefined {
  return Effect.runSync(brandByHostnameEffect(hostname));
}

export const resolveBrandEffect = Effect.fn('web.brand.resolve')(function* (input: {
  hostname?: string;
  brandId?: string;
} = {}) {
  const fromHost = yield* brandByHostnameEffect(input.hostname);
  if (fromHost) return fromHost;

  const id = input.brandId ?? readViteBrandId();
  if (id && id in BRANDS) return BRANDS[id as BrandId];
  return OKRAPDF_BRAND;
});

export function resolveBrand(input: {
  hostname?: string;
  brandId?: string;
} = {}): BrandConfig {
  return Effect.runSync(resolveBrandEffect(input));
}

export const getBrandEffect = Effect.fn('web.brand.get')(function* () {
  if (cached) return cached;
  const hostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
  cached = yield* resolveBrandEffect({ hostname });
  return cached;
});

export function getBrand(): BrandConfig {
  return Effect.runSync(getBrandEffect());
}

export function resetBrandCache(): void {
  cached = null;
}

export function isAppHostnameEffect(hostname: string): Effect.Effect<boolean> {
  return Effect.sync(() => Object.values(BRANDS).some((brand) => brand.hosts.app.includes(hostname)));
}

export function isAppHostname(hostname: string): boolean {
  return Effect.runSync(isAppHostnameEffect(hostname));
}

export function isMarketingHostnameEffect(hostname: string): Effect.Effect<boolean> {
  return Effect.sync(() =>
    Object.values(BRANDS).some(
      (brand) => brand.hosts.apex.includes(hostname) || brand.hosts.www.includes(hostname),
    ),
  );
}

export function isMarketingHostname(hostname: string): boolean {
  return Effect.runSync(isMarketingHostnameEffect(hostname));
}

export function allUiHosts(): Set<string> {
  const hosts = new Set<string>();
  for (const brand of Object.values(BRANDS)) {
    for (const host of [...brand.hosts.apex, ...brand.hosts.www, ...brand.hosts.app]) {
      hosts.add(host);
    }
  }
  return hosts;
}

export const appUrlEffect = Effect.fn('web.brand.appUrl')(function* (
  path = '/',
  brand?: BrandConfig,
) {
  const resolved = brand ?? (yield* getBrandEffect());
  return yield* Effect.sync(() => {
    const origin = resolved.origins.app.replace(/\/$/, '');
    if (!path || path === '/') return `${origin}/`;
    return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
  });
});

export function appUrl(path = '/', brand: BrandConfig = getBrand()): string {
  return Effect.runSync(appUrlEffect(path, brand));
}

export const marketingUrlEffect = Effect.fn('web.brand.marketingUrl')(function* (
  path = '/',
  brand?: BrandConfig,
) {
  const resolved = brand ?? (yield* getBrandEffect());
  return yield* Effect.sync(() => {
    const origin = resolved.origins.marketing.replace(/\/$/, '');
    if (!path || path === '/') return `${origin}/`;
    return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
  });
});

export function marketingUrl(path = '/', brand: BrandConfig = getBrand()): string {
  return Effect.runSync(marketingUrlEffect(path, brand));
}
